import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { DialogV2 } from '@withjoy/joykit';
import type { PayoutAccountInfo } from './CreditCardSetUp.context';
import { Introduction } from './steps/Introduction';
import { InitHostForm } from './steps/InitHostForm';
import { OnboardingStatus } from './steps/OnboardingStatus';
import { ReadyToUse } from './steps/ReadyToUse';
import { pxToRem } from '@withjoy/joykit/theme';
import { useFeatureValue } from '@shared/core/featureFlags';
import { useWindowFunction } from '@shared/hooks/useWindowFunction';
import { useCreditCardForCashEligibility } from './useCreditCardForCashEligibility';
import { AllyPromoStep } from './steps/AllyPromoStep';
import { useAdminRegistryTelemetry } from '../../AdminRegistry.telemetry';
import { DEFAULT_CURRENCY } from '@shared/utils/currency/constants';

const ALLY_PROMO_AUTO_ADVANCE_MS = 3000;

type DialogStep = 'introduction' | 'initHostForm' | 'ally' | 'onboarding' | 'readyToUse';

type CreditCardSetupInitiatorDialogProps = Readonly<{
  isOpen: boolean;
  onClose: () => void;
  isOwner: boolean;
  payoutAccountInfo?: PayoutAccountInfo | null;
  initKYCOnboarding: () => void;
  initPayoutAccountLoading: boolean;
  initKYCLaterOnboarding: (firstName: string, lastName: string, currencyCode: string, countryCode: string) => Promise<boolean>;
  initKYCLaterLoading: boolean;
  maxAttemptsReached: boolean;
  isValidUSAddress: boolean;
  isLocaleSupported: boolean;
  shouldSkipAddressValidation: boolean;
  handleInvalidShippingAddress?: (firstName?: string, lastName?: string) => void;
  handleChangeCurrency?: () => void;
  registryCurrencyCode?: string;
  shippingCountryCode?: string;
}>;

export const CreditCardSetupInitiatorDialog: React.FC<CreditCardSetupInitiatorDialogProps> = ({
  isOpen,
  onClose,
  isOwner,
  payoutAccountInfo,
  initKYCOnboarding,
  initPayoutAccountLoading,
  initKYCLaterOnboarding,
  initKYCLaterLoading,
  maxAttemptsReached,
  isValidUSAddress,
  isLocaleSupported,
  shouldSkipAddressValidation,
  handleInvalidShippingAddress,
  handleChangeCurrency,
  registryCurrencyCode,
  shippingCountryCode,
}) => {
  const { canEnableCreditCard, registryKycLaterEnabled } = useCreditCardForCashEligibility();
  const telemetry = useAdminRegistryTelemetry();
  const { value: activePartner } = useFeatureValue('activeCashFundPartner');
  const { value: createFundPartner } = useFeatureValue('enableCashFundPartnerCreateFundDialog');
  const showAllyPromo = activePartner === 'ally' && createFundPartner === 'ally';
  const openWindow = useWindowFunction('open');
  const allyDismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingHostDataRef = useRef<{ firstName: string; lastName: string; requiresAddressPrompt: boolean } | null>(null);
  const [overrideStep, setOverrideStep] = useState<DialogStep | null>(null);

  useEffect(() => {
    return () => {
      if (allyDismissTimerRef.current) clearTimeout(allyDismissTimerRef.current);
    };
  }, []);

  const activeStep: DialogStep = useMemo(() => {
    if (overrideStep) return overrideStep;
    if (canEnableCreditCard) return 'readyToUse';
    if (payoutAccountInfo?.status === 'pending') return 'onboarding';
    return registryKycLaterEnabled && isOwner ? 'initHostForm' : 'introduction';
  }, [overrideStep, payoutAccountInfo?.status, canEnableCreditCard, registryKycLaterEnabled, isOwner]);

  useEffect(() => {
    if (activeStep === 'ally') {
      telemetry.transferDialogBankPromoViewed('ally', 'HostFormDialog');
    }
  }, [activeStep, telemetry]);

  const handleClose = useCallback(() => {
    if (allyDismissTimerRef.current) {
      clearTimeout(allyDismissTimerRef.current);
      allyDismissTimerRef.current = null;
    }
    pendingHostDataRef.current = null;
    setOverrideStep(null);
    onClose();
  }, [onClose]);

  const proceedAfterAlly = useCallback(() => {
    const pending = pendingHostDataRef.current;
    pendingHostDataRef.current = null;
    if (pending && pending.requiresAddressPrompt && handleInvalidShippingAddress) {
      handleInvalidShippingAddress(pending.firstName, pending.lastName);
      handleClose();
    } else {
      handleClose();
    }
  }, [handleInvalidShippingAddress, handleClose]);

  const handleInitHostFormSubmit = useCallback(
    async (firstName: string, lastName: string, currencyCode: string, countryCode: string) => {
      const success = await initKYCLaterOnboarding(firstName, lastName, currencyCode, countryCode);
      if (success) {
        const requiresAddressPrompt = currencyCode === DEFAULT_CURRENCY && !isValidUSAddress;
        if (showAllyPromo) {
          pendingHostDataRef.current = { firstName, lastName, requiresAddressPrompt };
          setOverrideStep('ally');
        } else if (requiresAddressPrompt && handleInvalidShippingAddress) {
          handleInvalidShippingAddress(firstName, lastName);
          handleClose();
        } else {
          handleClose();
        }
      }
    },
    [initKYCLaterOnboarding, isValidUSAddress, handleInvalidShippingAddress, handleClose, showAllyPromo],
  );

  const handleAllyPromoAdvance = useCallback(() => {
    telemetry.transferDialogBankPromoInteracted('open_account', 'ally', 'HostFormDialog');
    openWindow('https://www.ally.com/', '_blank', 'noopener,noreferrer');
    if (allyDismissTimerRef.current) clearTimeout(allyDismissTimerRef.current);
    allyDismissTimerRef.current = setTimeout(() => {
      allyDismissTimerRef.current = null;
      proceedAfterAlly();
    }, ALLY_PROMO_AUTO_ADVANCE_MS);
  }, [telemetry, openWindow, proceedAfterAlly]);

  const handleAllyPromoSkip = useCallback(() => {
    if (allyDismissTimerRef.current) {
      clearTimeout(allyDismissTimerRef.current);
      allyDismissTimerRef.current = null;
    }
    telemetry.transferDialogBankPromoInteracted('skip', 'ally', 'HostFormDialog');
    proceedAfterAlly();
  }, [telemetry, proceedAfterAlly]);

  const handleIntroductionInvalidAddress = useCallback(() => {
    handleInvalidShippingAddress?.();
    handleClose();
  }, [handleInvalidShippingAddress, handleClose]);

  return (
    <DialogV2
      onClose={handleClose}
      isOpen={isOpen}
      size={activeStep === 'initHostForm' || activeStep === 'ally' ? 'lg' : undefined}
      overrides={
        activeStep !== 'initHostForm' && activeStep !== 'ally'
          ? {
              Content: {
                props: {
                  width: pxToRem(768),
                },
              },
            }
          : undefined
      }
    >
      <DialogV2.CloseButton />
      {activeStep === 'introduction' && (
        <Introduction
          payoutAccountInfo={payoutAccountInfo}
          initKYCOnboarding={initKYCOnboarding}
          isOwner={isOwner}
          initPayoutAccountLoading={initPayoutAccountLoading}
          isValidUSAddress={isValidUSAddress}
          isLocaleSupported={isLocaleSupported}
          shouldSkipAddressValidation={shouldSkipAddressValidation}
          handleInvalidShippingAddress={handleIntroductionInvalidAddress}
          handleChangeCurrency={handleChangeCurrency}
        />
      )}
      {activeStep === 'ally' && (
        <AllyPromoStep
          onAdvance={handleAllyPromoAdvance}
          onSkip={handleAllyPromoSkip}
        />
      )}
      {activeStep === 'initHostForm' && (
        <InitHostForm
          onSubmit={handleInitHostFormSubmit}
          loading={initKYCLaterLoading}
          registryCurrencyCode={registryCurrencyCode}
          shippingCountryCode={shippingCountryCode}
        />
      )}
      {activeStep === 'onboarding' && payoutAccountInfo && (
        <OnboardingStatus
          onboardingUrl={payoutAccountInfo.onboardingUrl}
          maxAttemptsReached={maxAttemptsReached}
          status={payoutAccountInfo.status}
        />
      )}
      {activeStep === 'readyToUse' && <ReadyToUse onClose={handleClose} />}
    </DialogV2>
  );
};
